import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, CheckCircle, XCircle, AlertTriangle, User, Calendar,
  DollarSign, Eye, TrendingDown, Briefcase
} from 'lucide-react';
import InfoTooltip from './components/common/InfoTooltip';

// Deal Desk threshold - quotes with ARR >= this amount are flagged
const DEAL_DESK_THRESHOLD = 200000;

/**
 * Approval Review View
 * Shows pending approvals, discounted quotes, and threshold breaches
 * Only accessible to managers and admins
 */
const ApprovalReviewView = ({ onViewQuote }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [discountedQuotes, setDiscountedQuotes] = useState([]);
  const [breachHistory, setBreachHistory] = useState([]);
  const [dealDeskQuotes, setDealDeskQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectFeedback, setRejectFeedback] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch all four data sets in parallel
      const [pendingRes, discountedRes, breachesRes, dealDeskRes] = await Promise.all([
        fetch(`${API_URL}/api/approvals/pending`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/approvals/discounted`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/approvals/breaches`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/approvals/deal-desk?threshold=${DEAL_DESK_THRESHOLD}`, { headers, credentials: 'include' })
      ]);

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingQuotes(Array.isArray(data) ? data : []);
      }
      if (discountedRes.ok) {
        const data = await discountedRes.json();
        setDiscountedQuotes(Array.isArray(data) ? data : []);
      }
      if (breachesRes.ok) {
        const data = await breachesRes.json();
        setBreachHistory(Array.isArray(data) ? data : []);
      }
      if (dealDeskRes.ok) {
        const data = await dealDeskRes.json();
        setDealDeskQuotes(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching approval data:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDealDeskApprove = async (quoteId) => {
    if (!window.confirm('Approve this high-value deal through Deal Desk?')) return;

    setActionLoading(quoteId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/deal-desk-approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include'
      });

      if (response.ok) {
        // Remove from deal desk list
        setDealDeskQuotes(prev => prev.filter(q => q.id !== quoteId));
        alert('Quote approved through Deal Desk');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to approve through Deal Desk');
      }
    } catch (error) {
      console.error('Error approving through Deal Desk:', error);
      alert('Failed to approve through Deal Desk');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/quotes/${quoteId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include'
      });

      if (response.ok) {
        // Remove from pending list
        setPendingQuotes(prev => prev.filter(q => q.id !== quoteId));
        alert('Quote approved successfully');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to approve quote');
      }
    } catch (error) {
      console.error('Error approving quote:', error);
      alert('Failed to approve quote');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectFeedback.trim()) {
      alert('Please provide feedback for the rejection');
      return;
    }

    setActionLoading(rejectModal.id);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/quotes/${rejectModal.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include',
        body: JSON.stringify({ feedback: rejectFeedback })
      });

      if (response.ok) {
        setPendingQuotes(prev => prev.filter(q => q.id !== rejectModal.id));
        setRejectModal(null);
        setRejectFeedback('');
        alert('Quote rejected and returned to draft');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to reject quote');
      }
    } catch (error) {
      console.error('Error rejecting quote:', error);
      alert('Failed to reject quote');
    } finally {
      setActionLoading(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDiscounts = (quoteData) => {
    const discounts = quoteData?.discounts || {};
    return Object.entries(discounts)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({ category: key, percent: value }));
  };

  const renderBreaches = (breaches) => {
    if (!breaches || breaches.length === 0) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {breaches.map((breach, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"
          >
            <AlertTriangle size={12} />
            {breach.category}: {breach.discountApplied}% (max {breach.maxAllowed}%)
          </span>
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'pending', label: 'Pending Approvals', icon: Clock, count: pendingQuotes.length, tooltip: 'Quotes with discounts exceeding configured thresholds awaiting manager approval.' },
    { id: 'dealdesk', label: 'Deal Desk', icon: Briefcase, count: dealDeskQuotes.length, highlight: true, tooltip: 'High-value quotes ($200K+ ARR) requiring executive review before finalizing.' },
    { id: 'discounted', label: 'Discount Review', icon: TrendingDown, count: discountedQuotes.length, tooltip: 'All quotes with any discounts applied for review and auditing purposes.' },
    { id: 'breaches', label: 'Threshold Breaches', icon: AlertTriangle, count: breachHistory.length, tooltip: 'Historical record of quotes that exceeded discount thresholds when submitted.' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center gap-2">
          Approval Review
          <InfoTooltip
            text="Managers and admins can approve or reject quotes here. Pending Approvals shows quotes with discounts exceeding thresholds. Deal Desk shows high-value quotes ($200K+ ARR) requiring executive review."
            position="right"
          />
        </h1>
        <p className="text-slate-600">Review pending quotes, discounts, and threshold violations</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-xl shadow-sm p-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            title={tab.tooltip}
          >
            <tab.icon size={18} />
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-200 text-slate-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
            Loading...
          </div>
        ) : (
          <>
            {/* Pending Approvals Tab */}
            {activeTab === 'pending' && (
              <div className="divide-y divide-slate-100">
                {pendingQuotes.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
                    <p className="text-lg font-medium">All caught up!</p>
                    <p className="text-sm mt-1">No quotes pending approval</p>
                  </div>
                ) : (
                  pendingQuotes.map((quote) => (
                    <div key={quote.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono font-semibold text-blue-600">
                              {quote.quote_number}
                            </span>
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                              Pending Approval
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-800 mb-1">
                            {quote.customer_name}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {quote.created_by_name || 'Unknown'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(quote.submitted_at || quote.updated_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign size={14} />
                              {formatCurrency(quote.total_arr)}
                            </span>
                          </div>

                          {/* Threshold Breaches */}
                          {quote.threshold_breaches && renderBreaches(
                            typeof quote.threshold_breaches === 'string'
                              ? JSON.parse(quote.threshold_breaches)
                              : quote.threshold_breaches
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => onViewQuote?.(quote)}
                            className="px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1"
                          >
                            <Eye size={16} />
                            View
                          </button>
                          <button
                            onClick={() => handleApprove(quote.id)}
                            disabled={actionLoading === quote.id}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                            title="Approve this quote and move it to Approved status. Sales rep will be notified."
                          >
                            {actionLoading === quote.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            ) : (
                              <CheckCircle size={16} />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              setRejectModal(quote);
                              setRejectFeedback('');
                            }}
                            disabled={actionLoading === quote.id}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                            title="Reject and return to draft. You'll provide feedback for the sales rep."
                          >
                            <XCircle size={16} />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Deal Desk Tab - High Value Deals */}
            {activeTab === 'dealdesk' && (
              <div className="divide-y divide-slate-100">
                {dealDeskQuotes.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <Briefcase size={48} className="mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No high-value deals</p>
                    <p className="text-sm mt-1">Quotes with ARR {formatCurrency(DEAL_DESK_THRESHOLD)}+ will appear here</p>
                  </div>
                ) : (
                  <>
                    <div className="px-6 py-3 bg-amber-50 border-b border-amber-200">
                      <p className="text-sm text-amber-800 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        <strong>Deal Desk Review Required:</strong> Quotes with ARR {formatCurrency(DEAL_DESK_THRESHOLD)} or more require executive review
                      </p>
                    </div>
                    {dealDeskQuotes.map((quote) => (
                      <div key={quote.id} className="p-6 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-mono font-semibold text-blue-600">
                                {quote.quote_number}
                              </span>
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-bold flex items-center gap-1">
                                <Briefcase size={12} />
                                Deal Desk
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                quote.status === 'approved' ? 'bg-green-100 text-green-700' :
                                quote.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                                quote.status === 'sent' ? 'bg-purple-100 text-purple-700' :
                                quote.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                                quote.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {quote.status?.replace('_', ' ')}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-1">
                              {quote.customer_name}
                            </h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-2">
                              <span className="flex items-center gap-1">
                                <User size={14} />
                                {quote.created_by_name || 'Unknown'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {formatDate(quote.updated_at)}
                              </span>
                              <span className="flex items-center gap-1 font-semibold text-green-600">
                                <DollarSign size={14} />
                                {formatCurrency(quote.total_arr)} ARR
                              </span>
                              {quote.total_services > 0 && (
                                <span className="flex items-center gap-1 text-purple-600">
                                  <DollarSign size={14} />
                                  {formatCurrency(quote.total_services)} Services
                                </span>
                              )}
                            </div>

                            {/* Industry & Region */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {quote.industry && (
                                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                  {quote.industry}
                                </span>
                              )}
                              {quote.region && (
                                <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                                  {quote.region}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => onViewQuote?.(quote)}
                              className="px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Eye size={16} />
                              View
                            </button>
                            <button
                              onClick={() => handleDealDeskApprove(quote.id)}
                              disabled={actionLoading === quote.id}
                              className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                              {actionLoading === quote.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                              ) : (
                                <CheckCircle size={16} />
                              )}
                              Approve
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Discount Review Tab */}
            {activeTab === 'discounted' && (
              <div className="divide-y divide-slate-100">
                {discountedQuotes.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <TrendingDown size={48} className="mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No discounted quotes</p>
                    <p className="text-sm mt-1">Quotes with discounts will appear here</p>
                  </div>
                ) : (
                  discountedQuotes.map((quote) => (
                    <div key={quote.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono font-semibold text-blue-600">
                              {quote.quote_number}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              quote.status === 'approved' ? 'bg-green-100 text-green-700' :
                              quote.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                              quote.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {quote.status}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-800 mb-1">
                            {quote.customer_name}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-2">
                            <span className="flex items-center gap-1">
                              <DollarSign size={14} />
                              {formatCurrency(quote.total_arr)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(quote.updated_at)}
                            </span>
                          </div>

                          {/* Discounts Applied */}
                          <div className="flex flex-wrap gap-2">
                            {getDiscounts(quote.quote_data).map((discount, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                              >
                                <TrendingDown size={12} />
                                {discount.category}: {discount.percent}%
                              </span>
                            ))}
                          </div>
                        </div>

                        <button
                          onClick={() => onViewQuote?.(quote)}
                          className="px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1"
                        >
                          <Eye size={16} />
                          View
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Threshold Breaches Tab */}
            {activeTab === 'breaches' && (
              <div className="divide-y divide-slate-100">
                {breachHistory.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <AlertTriangle size={48} className="mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No threshold breaches</p>
                    <p className="text-sm mt-1">Historical threshold violations will appear here</p>
                  </div>
                ) : (
                  breachHistory.map((quote) => (
                    <div key={quote.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono font-semibold text-blue-600">
                              {quote.quote_number}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              quote.status === 'approved' ? 'bg-green-100 text-green-700' :
                              quote.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                              quote.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {quote.status}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-800 mb-1">
                            {quote.customer_name}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {quote.created_by_name || 'Unknown'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              Breached: {formatDate(quote.breach_date)}
                            </span>
                          </div>

                          {/* Threshold Breaches */}
                          {quote.threshold_breaches && renderBreaches(
                            typeof quote.threshold_breaches === 'string'
                              ? JSON.parse(quote.threshold_breaches)
                              : quote.threshold_breaches
                          )}
                        </div>

                        <button
                          onClick={() => onViewQuote?.(quote)}
                          className="px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1"
                        >
                          <Eye size={16} />
                          View
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-red-50">
              <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                <XCircle size={20} />
                Reject Quote
              </h3>
            </div>
            <div className="p-6">
              <p className="text-slate-600 mb-4">
                You are about to reject quote <strong className="text-slate-800">{rejectModal.quote_number}</strong> for{' '}
                <strong className="text-slate-800">{rejectModal.customer_name}</strong>.
                This will return the quote to draft status.
              </p>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Rejection Feedback (Required)
              </label>
              <textarea
                value={rejectFeedback}
                onChange={(e) => setRejectFeedback(e.target.value)}
                placeholder="Provide feedback for the sales rep..."
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={4}
              />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setRejectModal(null);
                  setRejectFeedback('');
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectFeedback.trim() || actionLoading === rejectModal.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === rejectModal.id ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <XCircle size={16} />
                )}
                Reject Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalReviewView;
